import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalDropdown from '../../component/ModalDropdown';
import {
  getNotificationList,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../redux/reducers/ccSlice';

const NotificationDropdown = (props) => {
  const { isOpen = false, closePopUp, notifymodal = false, onCountUpdate } = props;
  const [notificationList, setNotificationList] = useState([]);
  const navigate = useNavigate();
  const listPayload = {
    page: 1,
    limit: 20,
    userType: 'FIELD_OFFICER',
  };

  useEffect(() => {
    if (notifymodal) {
      getNotificationList(listPayload)
        .then((response) => {
          if (response?.data?.notifications) {
            setNotificationList(response?.data?.notifications || []);
          } else {
            setNotificationList([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching notifications:', error);
          setNotificationList([]);
        });
    }
  }, [notifymodal]);

  const markAsRead = (id) => {
    const updatedNotifications = notificationList.map((notification) =>
      notification.id === id ? { ...notification, isRead: true } : notification
    );
    const readNotificationPayload = {
      notificationId: id,
    };
    markNotificationAsRead(readNotificationPayload)
      .then(() => {
        setNotificationList(updatedNotifications);
        // Notify parent component about count update
        if (onCountUpdate) {
          onCountUpdate();
        }
      })
      .catch((error) => {
        console.error('Error marking notification as read:', error);
      });
  };

  const markAllAsRead = () => {
    const updatedNotifications = notificationList.map((notification) => ({
      ...notification,
      isRead: true,
    }));

    const readAllPayload = {
      notificationIds: [...new Set(notificationList.map((notification) => notification.id))],
    };

    markAllNotificationsAsRead(readAllPayload)
      .then(() => {
        setNotificationList(updatedNotifications);
        // Notify parent component about count update
        if (onCountUpdate) {
          onCountUpdate();
        }
      })
      .catch((error) => {
        console.error('Error marking all notifications as read:', error);
      });
  };

  const handleNotificationClick = (notification) => {
    const navigate_to = notification?.data?.wPath;
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (navigate_to) {
      closePopUp();
      navigate(navigate_to);
    }
  };

  const renderAvatar = (notification) => {
    return (
      <div className='w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 text-indigo-600'>
        {notification.user?.avatar ? (
          <img
            src={notification.user.avatar}
            alt={notification.user.name}
            className='w-full h-full object-cover rounded-full'
          />
        ) : (
          <span className='font-semibold'>{notification.title.charAt(0) || '?'}</span>
        )}
      </div>
    );
  };

  const unreadCount = notificationList.filter((item) => !item.isRead).length;

  return (
    <>
      <div className='relative' />
      <ModalDropdown isOpen={isOpen} closePopUp={closePopUp} notifymodal={notifymodal}>
        <div className='max-h-[500px] w-[420px] bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200'>
          {notificationList?.length > 0 ? (
            <>
              {/* Header */}
              <div className='bg-gray-50 px-5 py-4 border-b border-gray-200'>
                <div className='flex justify-between items-center'>
                  <div className='flex items-center gap-3'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'
                        stroke='#6b7280'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M13.73 21a2 2 0 0 1-3.46 0'
                        stroke='#6b7280'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    <h3 className='text-lg font-semibold text-gray-800'>
                      Notifications {unreadCount > 0 ? `(${unreadCount})` : ''}
                    </h3>
                  </div>

                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className='text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-indigo-50 transition-colors'
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M20 6L9 17l-5-5'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      Mark all read
                    </button>
                  )}
                </div>
              </div>

              {/* Notifications List */}
              <div className='max-h-[400px] overflow-y-auto'>
                {notificationList.map((notification, index) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-5 py-4 transition-colors hover:bg-gray-50 cursor-pointer ${
                      notification?.wPath ? 'cursor-pointer' : ''
                    } ${
                      !notification.isRead
                        ? 'bg-blue-50/50 border-l-3 border-l-indigo-500'
                        : 'border-l-3 border-l-transparent'
                    } ${index !== notificationList.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <div className='flex items-start'>
                      {renderAvatar(notification)}

                      <div className='flex-1 min-w-0'>
                        <div className='flex items-start justify-between'>
                          <div className='flex-1 min-w-0'>
                            <h4
                              className={`text-sm leading-5 mb-1 ${
                                !notification.isRead
                                  ? 'font-semibold text-gray-900'
                                  : 'font-medium text-gray-700'
                              }`}
                            >
                              {notification.title}
                            </h4>
                            <p
                              className={`text-sm leading-5 mb-2 ${
                                !notification.isRead ? 'text-gray-700' : 'text-gray-500'
                              }`}
                            >
                              {notification.body}
                            </p>

                            <div className='flex items-center gap-3'>
                              <span className='text-xs text-gray-500'>
                                {notification.timestamp}
                              </span>
                              {!notification.isRead && (
                                <span className='inline-flex items-center gap-1 text-xs text-indigo-600 font-medium'>
                                  <div className='w-1.5 h-1.5 bg-indigo-500 rounded-full'></div>
                                  New
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className='ml-3'>
                            {!notification.isRead ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification?.id);
                                }}
                                className='p-2 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors'
                                title='Mark as read'
                              >
                                <svg
                                  width='16'
                                  height='16'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  xmlns='http://www.w3.org/2000/svg'
                                >
                                  <path
                                    d='M20 6L9 17l-5-5'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </button>
                            ) : (
                              <div className='p-2 text-green-500' title='Read'>
                                <svg
                                  width='16'
                                  height='16'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  xmlns='http://www.w3.org/2000/svg'
                                >
                                  <path
                                    d='M20 6L9 17l-5-5'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className='h-64 flex flex-col items-center justify-center text-center px-6'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'
                    stroke='#9ca3af'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M13.73 21a2 2 0 0 1-3.46 0'
                    stroke='#9ca3af'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <h3 className='text-lg font-medium text-gray-700 mb-2'>No new notifications</h3>
              <p className='text-gray-500 text-sm'>You're all caught up!</p>
            </div>
          )}
        </div>
      </ModalDropdown>
    </>
  );
};

export default NotificationDropdown;
